import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import { getOverallScore, getProfileFeedback, mergeProfileFeedback } from './utils';

const DEBOUNCE_MS = 500;

const getSignature = (answer) => JSON.stringify(answer.answer);

// Manages AI feedback for the Organization Profile form. Feedback is generated
// per field on blur (requests are debounced so rapid blurs coalesce, and an
// unchanged answer is never re-graded) and merged on top of any previously
// stored feedback. Generated feedback is NOT persisted here; the caller persists
// it together with the profile on save (see `getFeedback`) so the profile and
// its feedback never get out of sync in the database.
//
// `initialFeedback` hydrates the displayed feedback from the persisted account
// record so existing feedback shows on page load. When not `enabled`
// (e.g. demo/sandbox mode, which has no backend) generation is skipped.
export const useProfileFeedback = ({ enabled = true, initialFeedback } = {}) => {
  const [feedbackByKey, setFeedbackByKey] = useState(initialFeedback ?? {});
  const [loadingByKey, setLoadingByKey] = useState({});

  // Keep the latest feedback in a ref so the debounced flush can merge against
  // it, and so the caller can read the freshest feedback at save time.
  const feedbackRef = useRef(feedbackByKey);
  feedbackRef.current = feedbackByKey;

  const pendingRef = useRef(new Map());
  const signaturesRef = useRef(new Map());
  const timerRef = useRef(null);

  // Re-hydrate when the persisted feedback changes (e.g. account finishes
  // loading), but don't clobber feedback the user just generated this session.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (initialFeedback && Object.keys(initialFeedback).length > 0) {
      hydratedRef.current = true;
      setFeedbackByKey((prev) => ({ ...initialFeedback, ...prev }));
    }
  }, [initialFeedback]);

  const flush = useCallback(async () => {
    const pending = Array.from(pendingRef.current.values());
    pendingRef.current = new Map();

    if (pending.length === 0) return;

    const clearLoading = () => {
      setLoadingByKey((prev) => {
        const next = { ...prev };
        pending.forEach((answer) => {
          delete next[answer.key];
        });
        return next;
      });
    };

    try {
      const feedback = await getProfileFeedback(pending);
      const merged = mergeProfileFeedback(feedbackRef.current, feedback);
      feedbackRef.current = merged;
      setFeedbackByKey(merged);
      clearLoading();
    } catch (error) {
      Sentry.captureException(error);
      // Drop signatures so these answers can be retried on the next blur.
      pending.forEach((answer) => signaturesRef.current.delete(answer.key));
      clearLoading();
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      flush();
    }, DEBOUNCE_MS);
  }, [flush]);

  // Queues the supplied answers for grading. Unchanged answers (same content as
  // the last request) are skipped so re-blurring a field does not re-call the
  // API. The actual request is debounced via scheduleFlush.
  const requestFeedback = useCallback(
    (answers) => {
      if (!enabled || !Array.isArray(answers) || answers.length === 0) return;

      const queued = [];

      answers.forEach((answer) => {
        const signature = getSignature(answer);
        if (signaturesRef.current.get(answer.key) === signature) return; // unchanged since last request
        signaturesRef.current.set(answer.key, signature);
        pendingRef.current.set(answer.key, answer);
        queued.push(answer.key);
      });

      if (queued.length === 0) return;

      setLoadingByKey((prev) => {
        const next = { ...prev };
        queued.forEach((key) => {
          // Distinguish a first-time evaluation from a re-grade of an answer
          // that already has feedback. The UI only shows the "updating" hint
          // for re-grades; this also avoids a flash of "updating" on initial
          // load (React 17 does not batch the feedback/loading state updates).
          next[key] = feedbackRef.current[key] ? 'regrade' : 'initial';
        });
        return next;
      });

      scheduleFlush();
    },
    [enabled, scheduleFlush],
  );

  const clearFeedback = useCallback((keys) => {
    if (!Array.isArray(keys) || keys.length === 0) return;

    keys.forEach((key) => {
      signaturesRef.current.delete(key);
      pendingRef.current.delete(key);
    });

    setFeedbackByKey((prev) => {
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });

    setLoadingByKey((prev) => {
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const overallScore = useMemo(() => getOverallScore(feedbackByKey), [feedbackByKey]);

  const isEvaluating = useMemo(() => Object.values(loadingByKey).some(Boolean), [loadingByKey]);

  // Returns the freshest feedback map (from the ref, so it reflects any flush
  // that completed since the last render). The caller persists this on save.
  const getFeedback = useCallback(() => feedbackRef.current, []);

  return {
    feedbackByKey,
    loadingByKey,
    overallScore,
    isEvaluating,
    requestFeedback,
    clearFeedback,
    getFeedback,
  };
};

export default useProfileFeedback;
